import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
// Added DollarSign and HelpCircle for the new links
import { 
  Menu, X, Shield, Users, Cpu, ChevronRight, 
  MessageSquare, Layers, HelpCircle 
} from 'lucide-react';

const Navbar = ({ isConnected }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => setIsOpen(false), [location]);

  // --- UPDATED NAVIGATION LOGIC ---
  const navLinks = [
    { name: 'Home', path: '/', icon: Shield },
    { name: 'Services', path: '/services', icon: Layers },
    { name: 'FAQ', path: '/faq', icon: HelpCircle },         // New FAQ Link
    { name: 'Our Protocol', path: '/design-specs', icon: Cpu },
    { name: 'The Firm', path: '/about', icon: Users },
  ];

  return (
    <nav className={`fixed w-full z-[200] transition-all duration-500 ${
      scrolled ? 'py-4 bg-white/98 backdrop-blur-xl border-b border-stone-100 shadow-sm' : 'py-8 bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
        
        <Link to="/" className="group flex flex-col relative">
          <div className="flex items-center gap-2">
            <span className="text-sm font-black tracking-[0.3em] text-stone-900 uppercase">
              MED <span className="text-amber-600 font-bold italic">Consulting Solutions</span>
            </span>
            <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-amber-500 animate-pulse' : 'bg-stone-300'}`} />
          </div>
          <span className="text-[9px] font-medium text-stone-400 uppercase tracking-[0.15em] group-hover:text-amber-800 transition-colors">
            Principal Oversight
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8 lg:gap-12">
          {navLinks.map((link) => (
            <Link 
              key={link.path} 
              to={link.path}
              className={`text-[10px] font-bold uppercase tracking-[0.25em] transition-all relative group ${
                location.pathname === link.path ? 'text-amber-700' : 'text-stone-500 hover:text-stone-900'
              }`}
            >
              {link.name}
              {location.pathname === link.path && (
                <motion.div layoutId="navUnderline" className="absolute -bottom-1 left-0 w-full h-px bg-amber-600" />
              )}
            </Link>
          ))}
          
          <Link 
            to="/book-consultation"
            className="group bg-stone-950 text-white px-6 py-3 rounded-full text-[9px] font-black uppercase tracking-[0.3em] hover:bg-amber-700 transition-all shadow-xl flex items-center gap-3"
          >
            Start a Briefing
            <MessageSquare size={12} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button onClick={() => setIsOpen(!isOpen)} className="md:hidden text-stone-900 p-3 bg-stone-50 rounded-full">
          {isOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="absolute top-full left-0 w-full bg-white border-b border-stone-200 md:hidden shadow-2xl"
          >
            <div className="p-8 space-y-8">
              <div className="grid grid-cols-1 gap-6">
                {navLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link key={link.path} to={link.path} className="flex items-center justify-between group">
                      <div className="flex items-center gap-5">
                        <div className="p-2 bg-stone-50 rounded-lg group-hover:bg-amber-50 transition-colors">
                          <Icon size={18} className="text-stone-400 group-hover:text-amber-600 transition-colors" />
                        </div>
                        <span className="text-sm font-bold uppercase tracking-widest text-stone-900">{link.name}</span>
                      </div>
                      <ChevronRight size={16} className="text-stone-300 group-hover:text-amber-600 group-hover:translate-x-1 transition-all" />
                    </Link>
                  );
                })}
              </div>
              <Link to="/book-consultation" className="block w-full bg-stone-950 text-white py-6 rounded-2xl text-center text-[11px] font-black uppercase tracking-[0.3em]">
                Request Principal Briefing
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;